import { COMMON_ORPC_ERROR_DEFS } from "@orpc/client";

type ORPCErrorCode = LooseToStrict<keyof typeof COMMON_ORPC_ERROR_DEFS>;

export type LooseToStrict<T> = T extends any ? (string extends T ? never : T) : never;

export type ErrorInput<C = unknown> = {
  message?: string;
  cause?: C;
  error?: unknown;
  orpcErrorCode?: ORPCErrorCode;
  data?: unknown;
  posthog?: Record<string, unknown>;
};

export type AnyBaseErrorClass<C = unknown> = new (input?: ErrorInput<C>) => __BASE_ERROR<C>;

export abstract class __BASE_ERROR<C = unknown> extends Error {
  abstract readonly _tag: string;
  override readonly cause?: C;
  readonly orpcErrorCode: ORPCErrorCode;
  readonly data?: unknown;
  readonly posthog?: Record<string, unknown>;

  constructor(input: {
    message?: string;
    cause?: C;
    orpcErrorCode: ORPCErrorCode;
    data?: unknown;
    posthog?: Record<string, unknown>;
  }) {
    super(input?.message || "Base error, no message provided");
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = this.constructor.name;
    this.cause = input?.cause;
    this.orpcErrorCode = input?.orpcErrorCode;
    this.data = input?.data;
    this.posthog = input?.posthog;
  }

  static resolveMessage<C>(input: ErrorInput<C> | undefined, fallback: string): string {
    if (input?.message) return input.message;
    if (input?.error instanceof Error && input.error.message) {
      return input.error.message;
    }
    if (typeof input?.error === "string") return input.error;
    return fallback;
  }

  static resolveCause<C>(input: ErrorInput<C> | undefined): C | undefined {
    if (input?.cause) return input.cause;
    if (input?.error instanceof Error && input.error.cause) {
      return input.error.cause as C;
    }
    return input?.error as C | undefined;
  }
}

export function makeErrorClass<C = unknown, T extends string = string>(
  tag: T,
  fallbackMessage: string,
  orpcErrorCode: ORPCErrorCode,
) {
  return class extends __BASE_ERROR<C> {
    readonly _tag = tag;
    constructor(input?: ErrorInput<C>) {
      super({
        message: __BASE_ERROR.resolveMessage(input, fallbackMessage),
        cause: __BASE_ERROR.resolveCause(input),
        orpcErrorCode: input?.orpcErrorCode ?? orpcErrorCode,
        data: input?.data,
        posthog: input?.posthog,
      });
    }
  };
}

// 500s
export class _UnknownError extends makeErrorClass("_UnknownError", "Unknown error", "INTERNAL_SERVER_ERROR") {}
export class _FetchError extends makeErrorClass("_FetchError", "Fetch error", "INTERNAL_SERVER_ERROR") {}
export class _DatabaseError extends makeErrorClass("_DatabaseError", "Database error", "INTERNAL_SERVER_ERROR") {}
export class _FileUploadError extends makeErrorClass(
  "_FileUploadError",
  "File upload error",
  "INTERNAL_SERVER_ERROR",
) {}

// 400s
export class BadRequestError extends makeErrorClass("BadRequestError", "Bad request", "BAD_REQUEST") {}
export class UnauthorizedError extends makeErrorClass("UnauthorizedError", "Unauthorized", "UNAUTHORIZED") {}
export class ForbiddenError extends makeErrorClass("ForbiddenError", "Forbidden", "FORBIDDEN") {}
export class NotFoundError extends makeErrorClass("NotFoundError", "Not found", "NOT_FOUND") {}
